/**
 * Recently viewed products. Browser-only: nothing is sent anywhere except the requests that fetch product cards
 * from this same store.
 *
 * 1. On product pages the product handle is saved in localStorage (most recent first, no duplicates, max 12).
 *    Only handles are stored. No cookies, no personal data.
 * 2. <recently-viewed> elements (sections/recently-viewed.liquid) read those handles, skip the product being viewed,
 *    and fetch the full custom product card for each remaining one through the Section Rendering API
 *    (section-rendering-wishlist-card, the same endpoint the wishlist page uses).
 * 3. The section stays hidden until at least one card has loaded. Sold-out and missing products are skipped.
 *
 * Every storage call is wrapped in try/catch: if storage is blocked the row simply never appears.
 */
(() => {
  if (window.__dhaaniRecentlyViewed) return;
  window.__dhaaniRecentlyViewed = true;

  const KEY = 'dhaani_recently_viewed';
  const MAX_STORED = 12;
  const HANDLE_PATTERN = /^[a-z0-9][a-z0-9_-]*$/i;
  const CARD_SECTION = 'section-rendering-wishlist-card';
  const CARD_SIZES = '(min-width: 990px) 25vw, (min-width: 750px) 33vw, 44vw';
  const MOBILE_QUERY = '(max-width: 749px)';

  /** @returns {string[]} */
  function readHandles() {
    try {
      const raw = window.localStorage.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((handle) => typeof handle === 'string' && HANDLE_PATTERN.test(handle)).slice(0, MAX_STORED);
    } catch (error) {
      return [];
    }
  }

  /** @param {string[]} handles */
  function writeHandles(handles) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(handles.slice(0, MAX_STORED)));
    } catch (error) {
      /* storage blocked or full: nothing to do */
    }
  }

  /** @param {string} handle */
  function recordView(handle) {
    if (!handle || !HANDLE_PATTERN.test(handle)) return;
    writeHandles([handle, ...readHandles().filter((existing) => existing !== handle)]);
  }

  /** @param {string} handle */
  function removeHandles(handlesToRemove) {
    if (!handlesToRemove.length) return;
    writeHandles(readHandles().filter((handle) => !handlesToRemove.includes(handle)));
  }

  /**
   * Fetches one product card.
   * @param {string} handle
   * @param {AbortSignal} signal
   * @returns {Promise<{state: 'ok', card: Element} | {state: 'sold-out'} | {state: 'gone'} | {state: 'error'}>}
   */
  async function fetchCard(handle, signal) {
    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}?section_id=${CARD_SECTION}`, {
        credentials: 'same-origin',
        signal,
      });
      if (response.status === 404 || response.status === 410) return { state: 'gone' };
      if (!response.ok) return { state: 'error' };

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = doc.querySelector('product-card');
      // A handle that no longer exists still answers 200 with an empty, disabled card that has no product link.
      if (!card || !card.querySelector('a[href*="/products/"]')) return { state: 'gone' };
      if (card.querySelector('.custom-card-btn[disabled]')) return { state: 'sold-out' };

      // Section rendering has no section index, so images would load eagerly at full width. Make them lazy and right-sized.
      card.querySelectorAll('img').forEach((img) => {
        img.loading = 'lazy';
        img.decoding = 'async';
        img.removeAttribute('fetchpriority');
        if (img.hasAttribute('srcset')) img.sizes = CARD_SIZES;
      });
      return { state: 'ok', card };
    } catch (error) {
      return { state: 'error' };
    }
  }

  class RecentlyViewed extends HTMLElement {
    /** @type {IntersectionObserver | null} */
    #observer = null;
    /** @type {AbortController | null} */
    #abort = null;

    connectedCallback() {
      if (this.dataset.rvReady) return;
      this.dataset.rvReady = 'true';

      if (this.dataset.showOnMobile === 'false' && window.matchMedia(MOBILE_QUERY).matches) return;
      if (this.#handles().length === 0) return;

      const sentinel = this.nextElementSibling instanceof HTMLElement && this.nextElementSibling.hasAttribute('data-rv-sentinel')
        ? this.nextElementSibling
        : null;

      // Keep roughly the right amount of room while hidden, so revealing the row does not push content around.
      if (sentinel) sentinel.style.height = `${this.#reservedHeight()}px`;

      if (!('IntersectionObserver' in window) || !sentinel) {
        this.#load(sentinel);
        return;
      }
      this.#observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.#observer?.disconnect();
          this.#observer = null;
          this.#load(sentinel);
        },
        { rootMargin: '400px 0px' }
      );
      this.#observer.observe(sentinel);
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
      this.#abort?.abort();
    }

    /** Handles to show: saved ones, newest first, minus the product being viewed. */
    #handles() {
      const current = this.dataset.currentHandle || '';
      return readHandles().filter((handle) => handle !== current);
    }

    /** Rough height of the finished row (heading, padding and grid rows), measured on the live layout. */
    #reservedHeight() {
      if (window.matchMedia(MOBILE_QUERY).matches) return 540;
      const limit = Math.min(12, Math.max(4, parseInt(this.dataset.limit || '8', 10) || 8));
      const columns = window.matchMedia('(min-width: 990px)').matches ? 4 : 3;
      const rows = Math.ceil(Math.min(this.#handles().length, limit) / columns);
      return 126 + 494 * Math.max(1, rows);
    }

    /** @param {HTMLElement | null} sentinel */
    async #load(sentinel) {
      const limit = Math.min(12, Math.max(4, parseInt(this.dataset.limit || '8', 10) || 8));
      const candidates = this.#handles().slice(0, limit + 4);
      const release = () => {
        if (sentinel) sentinel.style.height = '0';
      };
      if (candidates.length === 0) return release();

      this.#abort = new AbortController();
      const results = await Promise.all(candidates.map((handle) => fetchCard(handle, this.#abort.signal)));
      if (!this.isConnected) return;

      removeHandles(candidates.filter((_, index) => results[index].state === 'gone'));
      const cards = results.flatMap((result) => (result.state === 'ok' ? [result.card] : [])).slice(0, limit);
      if (cards.length === 0) return release();

      const list = this.querySelector('[data-rv-list]');
      if (!list) return release();
      list.replaceChildren(
        ...cards.map((card) => {
          const item = document.createElement('li');
          item.className = 'rv__item';
          item.append(document.importNode(card, true));
          return item;
        })
      );

      this.hidden = false;
      release();
      if (window.wishlist && typeof window.wishlist.initButtons === 'function') window.wishlist.initButtons();
    }
  }

  if (!customElements.get('recently-viewed')) customElements.define('recently-viewed', RecentlyViewed);

  // Record the visit as soon as the document is ready.
  const record = () => {
    const meta = document.querySelector('meta[name="rv-product-handle"]');
    if (meta instanceof HTMLMetaElement) recordView(meta.content);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', record, { once: true });
  else record();
})();
